/**
 * The assistant with nothing plugged into it.
 *
 * This exists so the Help button is never a dead end. A family that has not set
 * up a key — or has run out of credit, or is on a train with no signal — still
 * presses that button, and "the assistant is not configured" is a terrible thing
 * to find there. So the app answers for itself: it matches the question against
 * the how-tos, rules and limits it already knows, and gives back the same
 * walkthrough a model would have asked for.
 *
 * It is not clever and does not pretend to be. It recognises the two dozen
 * things people actually ask, in both languages, and says plainly when it does
 * not understand rather than inventing something. That is a much better floor
 * than an error message, and it makes the whole feature usable with no account
 * anywhere — which for an app whose whole premise is "no backend yet" is the
 * right default rather than a consolation prize.
 *
 * Everything it knows comes from `knowledge/appMap.ts`, the same file the model
 * reads. Teach the app a new screen and both get smarter together.
 */
import { HOWTOS, LIMITS, RULES, type HowTo } from '../knowledge/appMap';
import type { AskReply, AskRequest, AssistantProvider } from './types';

/** Lower-case, drop punctuation and Hebrew vowel marks, collapse spaces. */
function normalise(s: string): string {
  return s
    .toLowerCase()
    .replace(/[֑-ׇ]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/*
 * Words that carry no subject.
 *
 * This list started short and cost an answer for it: *"show me people that are
 * around my age"* was answered with the rule about Hebrew and English names,
 * because "are" appears three times in that rule's wording and nothing else in
 * the question appeared anywhere. Grammar words are the ones that turn up in
 * every sentence in the app, so any of them left in is a word that matches
 * everything and means nothing.
 */
const STOP = new Set([
  'a', 'an', 'the', 'i', 'me', 'my', 'mine', 'to', 'do', 'does', 'did', 'how', 'can', 'could', 'what', 'is', 'it', 'in', 'on', 'of', 'for', 'and', 'you', 'we', 'this', 'that', 'with', 'get', 'want', 'please', 'help',
  'am', 'are', 'be', 'been', 'was', 'were', 'has', 'have', 'had', 'will', 'would', 'shall', 'should', 'may', 'might', 'must',
  'he', 'she', 'they', 'them', 'their', 'his', 'her', 'hers', 'its', 'our', 'ours', 'your', 'yours', 'us', 'who', 'whom', 'whose',
  'but', 'or', 'nor', 'so', 'if', 'then', 'than', 'as', 'at', 'by', 'from', 'into', 'onto', 'over', 'under', 'about', 'up', 'down', 'out', 'off', 'too', 'very', 'just', 'also', 'again', 'there', 'here',
  'some', 'any', 'each', 'every', 'both', 'few', 'many', 'much', 'other', 'others', 'same', 'such', 'own', 'been',
  'את', 'של', 'אני', 'איך', 'מה', 'זה', 'אפשר', 'לי', 'עם', 'על', 'לא', 'יש',
  'הוא', 'היא', 'הם', 'הן', 'אנחנו', 'אתם', 'אתה', 'שלי', 'שלו', 'שלה', 'שלהם',
  'אם', 'כי', 'גם', 'רק', 'כבר', 'אבל', 'או', 'אז', 'כמו', 'עוד', 'מאוד', 'כדי', 'אל', 'כל', 'היה', 'הייתה', 'להיות',
]);

/**
 * Hebrew glues its small words onto the front of the next one.
 *
 * "ציר הזמן" and "ציר זמן" are the same thing said two ways — one has the
 * definite article stuck to the noun — and a matcher that compares them letter
 * by letter decides they are unrelated. That is not a corner case in an app
 * half of whose family speaks Hebrew: it is most of how people actually phrase a
 * question. So a word beginning with one of these particles contributes its stem
 * as well as itself, and either phrasing finds the other.
 */
const HE_PREFIXES = ['ה', 'ו', 'ב', 'ל', 'כ', 'מ', 'ש'];

function tokens(s: string): string[] {
  const out: string[] = [];
  for (const w of normalise(s).split(' ')) {
    if (w.length <= 1 || STOP.has(w)) continue;
    out.push(w);
    if (w.length >= 4 && HE_PREFIXES.includes(w[0])) {
      const stem = w.slice(1);
      if (!STOP.has(stem)) out.push(stem);
    }
  }
  return out;
}

/** How well a question matches one how-to. A whole phrase counts for far more than a stray word. */
function scoreHowTo(question: string, how: HowTo): number {
  const q = normalise(question);
  const qt = new Set(tokens(question));
  let score = 0;
  for (const phrase of how.asked) {
    const p = normalise(phrase);
    if (!p) continue;
    // Distinct words, for the same reason as `scoreText` below.
    const pt = [...new Set(tokens(phrase))];
    /*
     * A one-word trigger is matched as a *word*, and counts for much less.
     *
     * Two things went wrong when it was matched as a run of letters and scored
     * like a whole phrase. "ברית" — a brit — is sitting inside "בעברית", which
     * is simply the word "in Hebrew", so *"why is the Hebrew name blank"* in
     * Hebrew was answered with a walkthrough for adding a bris. And a single
     * word scoring ten meant one common word decided the whole answer: any
     * question mentioning Hebrew was sent to the language switch, whatever it
     * was actually asking.
     *
     * So a one-word trigger has to appear as a whole word, and is worth less
     * than a real phrase — enough to carry a bare "timeline" or "globe" to its
     * walkthrough, not enough to outrank a two- or three-word phrase that
     * matched the question exactly.
     */
    // One *word* — counted from the phrase itself, not from `pt`, which a
    // Hebrew stem can quietly turn into two entries.
    if (p.split(' ').filter(Boolean).length <= 1) {
      if (pt.some((w) => qt.has(w))) score += 7;
      continue;
    }
    if (q.includes(p)) score += 10 + p.length / 8;
    else {
      const hits = pt.filter((w) => qt.has(w)).length;
      if (hits === pt.length) score += 5;
      else score += hits * 1.5;
    }
  }
  for (const w of tokens(how.title.en)) if (qt.has(w)) score += 1;
  for (const w of tokens(how.title.he)) if (qt.has(w)) score += 1;
  return score;
}

/**
 * Rules and limits, scored the same way, for "why isn't this working".
 *
 * Both sides are counted as *sets*. Counting the rule's words one by one meant a
 * single word in the question could score three times over because the rule
 * happened to repeat it — which is how "are" once carried a question about ages
 * to an answer about Hebrew spelling. Overlap is about how many different things
 * two sentences have in common, not how often one of them says the same word.
 */
function scoreText(question: string, text: string): number {
  const qt = new Set(tokens(question));
  const tt = new Set(tokens(text));
  if (!tt.size) return 0;
  let hits = 0;
  for (const w of tt) if (qt.has(w)) hits += 1;
  // Long rules would otherwise win on sheer surface area.
  return (hits * hits) / Math.sqrt(tt.size);
}

/**
 * "Who is around my age?" — a question about the family, not about the app.
 *
 * The built-in provider is a matcher, so almost everything it answers is a
 * walkthrough or an explanation. This one is worth the exception: it is a
 * question people really ask, there is no model behind this provider to work it
 * out, and left alone it fell through to whichever rule happened to share a
 * grammar word with it. The answer is arithmetic on birth years — see
 * `bornNear` — so it can be done here properly rather than guessed at.
 */
const AGE = [
  'my age', 'same age', 'my generation', 'my own age', 'close to my age', 'near my age', 'around my age', 'about my age',
  'older than me', 'younger than me', 'my age group', 'same generation',
  'בגיל שלי', 'בני גילי', 'בן גילי', 'באותו גיל', 'אותו גיל', 'בגילי', 'בני דורי', 'אותו דור', 'מבוגר ממני', 'צעיר ממני',
];

const BROKEN = ['not work', 'doesnt work', 'does not work', 'isnt working', 'is not working', 'broken', 'bug', 'error', 'wrong', 'cant', 'cannot', 'wont', 'will not', 'stuck', 'missing', 'gone', 'disappeared', 'לא עובד', 'תקלה', 'באג', 'שגיאה', 'נעלם', 'חסר', 'לא מצליח'];
const WISH = ['i wish', 'it would be nice', 'feature', 'suggestion', 'should be able', 'please add', 'can you add', 'הצעה', 'הייתי רוצה', 'כדאי להוסיף'];

function mentions(question: string, phrases: string[]): boolean {
  const q = normalise(question);
  return phrases.some((p) => q.includes(normalise(p)));
}

/**
 * Built in, needs nothing, works offline. Matches the question and answers with
 * the same tool calls a model would have made.
 */
export function createLocalProvider(): AssistantProvider {
  return {
    id: 'local',
    label: 'Built in',
    ready: () => true,
    notReadyKey: () => null,
    async ask({ turns }: AskRequest): Promise<AskReply> {
      // One pass only. If the app has already fed a tool result back, the answer
      // was given on the round before and there is nothing to add.
      const last = turns[turns.length - 1];
      if (last?.results?.length) return { text: '', calls: [] };

      const question = [...turns].reverse().find((t) => t.role === 'user')?.text ?? '';
      if (!question.trim()) return { text: '', calls: [{ id: 'l0', name: 'suggest', input: {} }] };

      // Before any text matching: a question about who is the same age as them
      // is answered from the records, not from the app's own documentation.
      if (mentions(question, AGE)) {
        return { text: '', calls: [{ id: 'la', name: 'sameAge', input: {} }] };
      }

      const best = HOWTOS.map((h) => ({ h, score: scoreHowTo(question, h) })).sort((a, b) => b.score - a.score)[0];
      // Both languages are scored: somebody asks in Hebrew about a rule written
      // in English, and the Hebrew wording of that same rule is what matches.
      const rule = RULES.map((r) => ({ r, score: Math.max(scoreText(question, r.answers) * 1.4, scoreText(question, r.says.en), scoreText(question, r.says.he)) })).sort((a, b) => b.score - a.score)[0];
      const limit = LIMITS.map((l) => ({ l, score: Math.max(scoreText(question, l.says.en), scoreText(question, l.says.he)) })).sort((a, b) => b.score - a.score)[0];

      // A clear how-to beats an explanation: showing is better than telling.
      if (best && best.score >= 6) {
        return { text: '', calls: [{ id: 'l1', name: 'guide', input: { howto: best.h.id } }] };
      }

      // "Why isn't this working" — a rule or a known limit usually explains it.
      const complaining = mentions(question, BROKEN);
      if (rule && rule.score >= 2.2 && rule.score >= (limit?.score ?? 0)) {
        return { text: '', calls: [{ id: 'l2', name: 'explain', input: { rule: rule.r.id, offerReport: complaining } }] };
      }
      if (limit && limit.score >= 2.2) {
        return { text: '', calls: [{ id: 'l3', name: 'explain', input: { limit: limit.l.id, offerReport: complaining } }] };
      }

      // Nothing matched. If they sound unhappy, get it to the administrators
      // rather than leaving them with a shrug.
      if (complaining || mentions(question, WISH)) {
        return { text: '', calls: [{ id: 'l4', name: 'offerReport', input: { kind: mentions(question, WISH) ? 'idea' : 'fault', detail: question.slice(0, 500) } }] };
      }

      // A weak how-to match is still better than nothing, offered as a question.
      if (best && best.score >= 2.5) {
        return { text: '', calls: [{ id: 'l5', name: 'suggest', input: { howtos: [best.h.id], unsure: true } }] };
      }
      return { text: '', calls: [{ id: 'l6', name: 'suggest', input: { unsure: true } }] };
    },
  };
}
